import EditorReadonly from "components/general/EditorReadonly";
import { apisStore } from "context/GlobalStates";
import { Column } from "primereact/column";
import { ConfirmPopup } from "primereact/confirmpopup";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { useEffect, useState } from "react";
import NotificationPopUp from "util/NotificationPopUp";
import { JobRunnerTabTypes } from "./JobRunner";
import Utils from "util/Utils";
import {EasasScheduledTasksApi, JobInfoDTO, ScheduledTaskHistoryDTO} from "../../../serviceapi/jobRunnerApi";

interface JobHistoryDialogProps {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  job: JobInfoDTO;
}

const JobHistoryDialog = ({
  isVisible,
  setIsVisible,
  job,
}: JobHistoryDialogProps) => {
  const [activeTab, setActiveTab] = useState<JobRunnerTabTypes>(
    JobRunnerTabTypes.CONFIG
  );
  const [currentHistory, setCurrentHistory] = useState<
    ScheduledTaskHistoryDTO[]
  >([]);

  const [selectedPipeLine, setSelectedPipeLine] = useState<any>(undefined);
  const apiScheduled = apisStore.getState()?.jobRunner
    ?.easasScheduledTasksApi as EasasScheduledTasksApi;

  const fetchHistory = async () => {
    try {
      const history = await apiScheduled.getAirflowTaskHistories(
        "1",
        job.taskId
      );
      setCurrentHistory(history.data);
    } catch (error) {
      NotificationPopUp.show("Failed to fetch history", "error");
    }
  };

  useEffect(() => {
    if (isVisible) {
      fetchHistory();
    }
  }, [isVisible]);

  const isValidJson = (str: string) => {
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  };

  const getTabContent = () => {
    if (!selectedPipeLine) {
      return "Please select a pipeline to view the configuration.";
    }

    const configValue = selectedPipeLine.config || "// No config available";

    if (!isValidJson(configValue)) {
      console.warn("Config is not JSON. Returning raw script.");
      return configValue;
    }

    try {
      const parsedConfig = JSON.parse(configValue);

      if (activeTab === JobRunnerTabTypes.CONFIG) {
        return JSON.stringify(parsedConfig, null, 4);
      } else if (activeTab === JobRunnerTabTypes.CODE) {
        return parsedConfig.code || "// Code is empty or undefined.";
      } else if (activeTab === JobRunnerTabTypes.TEMPLATE) {
        return JSON.stringify(parsedConfig.templateInfo, null, 4);
      }

      return JSON.stringify(parsedConfig, null, 4);
    } catch (error) {
      console.error("Error parsing config:", error);
      return "Error loading configuration.";
    }
  };

  const changeOnTemplate = (rowData: ScheduledTaskHistoryDTO) => {
    return Utils.formatISOToBasicDateFormat(rowData.changeOn as string);
  };

  return (
    <Dialog
      visible={isVisible}
      style={{
        width: "80%",
        height: "100%",
      }}
      modal={false}
      resizable
      onHide={() => setIsVisible(false)}
      header="Job History"
    >
      <div className={"writer-history-full-window"}>
        <div className={"left-side"}>
          <DataTable
            value={currentHistory}
            size="small"
            sortField="changeOn"
            sortOrder={-1}
            selectionMode="single"
            selection={selectedPipeLine}
            onSelectionChange={(e) => setSelectedPipeLine(e.value)}
          >
            <Column field="label" header="Label" />
            <Column
              field="changeOn"
              header="Change on"
              body={changeOnTemplate}
            />
            <Column field="changeBy" header="Change by" />
          </DataTable>
        </div>
        <div className="right-side">
          <div className="button-container-scheme-picker">
            {[
              JobRunnerTabTypes.CONFIG,
              JobRunnerTabTypes.CODE,
              JobRunnerTabTypes.TEMPLATE,
            ].map((tab) => (
              <div
                key={tab}
                className={`tab-view-button ${
                  activeTab === tab ? "active" : ""
                }`}
                onClick={() => setActiveTab(tab as JobRunnerTabTypes)}
              >
                {tab}
              </div>
            ))}
          </div>
          <EditorReadonly text={getTabContent()} />
        </div>
        <ConfirmPopup />
      </div>
    </Dialog>
  );
};

export default JobHistoryDialog;
